import { notifications } from "@mantine/notifications"
import {
  useMutation,
  UseMutationOptions,
  useQuery,
} from "@tanstack/react-query"
import { AxiosError } from "axios"
import i18next from "i18next"
import { useTranslation } from "react-i18next"

import { CountryIso3166, LibraryResponses } from "@tfg/types/endpoints/library"
import {
  productionSitesQueryValidationType,
  riskQueryValidationType,
  SupplierGetQueryValidationType,
  supplierMonitoringFiltersValidationType,
  transferContactValidationType,
} from "@tfg/types/schemas/organization/schemas"
import { fetchDataOptionsParam } from "@tfg/types/schemas/products"

import { httpClient } from "~/backend/axios.ts"
import { queryClient } from "~/backend/queryClient.ts"

export const useListSuppliersQuery = ({
  fetchDataOptions,
  searchSupplier,
  filters,
  orderBy,
}: Omit<SupplierGetQueryValidationType, "filters"> &
  fetchDataOptionsParam & {
    filters: supplierMonitoringFiltersValidationType
  }) => {
  return useQuery<LibraryResponses.SuppliersResponse>(
    ["suppliers", fetchDataOptions, searchSupplier, filters, orderBy],
    async () => {
      const skip = fetchDataOptions.pageIndex * fetchDataOptions.pageSize
      const take = fetchDataOptions.pageSize
      const res = await httpClient.get<LibraryResponses.SuppliersResponse>(
        "suppliers/monitoring",
        {
          params: {
            skip,
            take,
            searchSupplier,
            filters: JSON.stringify(filters),
            orderBy: JSON.stringify(orderBy),
          },
        }
      )
      return res.data
    }
  )
}

export const useListSuppliersCountriesQuery = () => {
  return useQuery<CountryIso3166[]>(["suppliersCountries"], async () => {
    const res =
      await httpClient.get<LibraryResponses.SuppliersCountriesResponse>(
        "traceability/suppliers/countries"
      )
    return res.data?.countries || []
  })
}

export const useListSuppliersProductionSitesQuery = ({
  fetchDataOptions,
  searchProductionSite,
}: productionSitesQueryValidationType & fetchDataOptionsParam) => {
  return useQuery<LibraryResponses.SupplierProductionSitesResponse>(
    ["suppliers-prodsites", fetchDataOptions, searchProductionSite],
    async () => {
      const res =
        await httpClient.get<LibraryResponses.SupplierProductionSitesResponse>(
          "suppliers/production-sites",
          {
            headers: {
              "Accept-Language": i18next.language.toUpperCase(),
            },
            params: {
              skip: fetchDataOptions.pageIndex * fetchDataOptions.pageSize,
              take: fetchDataOptions.pageSize,
              searchProductionSite,
            },
          }
        )
      return res.data
    }
  )
}

export const useListSuppliersRiskQuery = ({
  fetchDataOptions,
  searchSupplier,
  filters,
}: riskQueryValidationType & fetchDataOptionsParam) => {
  return useQuery<LibraryResponses.SupplierRiskResponse>(
    ["suppliers", fetchDataOptions, searchSupplier, filters],
    async () => {
      const skip = fetchDataOptions.pageIndex * fetchDataOptions.pageSize
      const take = fetchDataOptions.pageSize
      const res = await httpClient.get<LibraryResponses.SupplierRiskResponse>(
        "suppliers/risk",
        {
          params: {
            skip,
            take,
            searchSupplier,
            filters: filters ? JSON.stringify(filters) : {},
          },
        }
      )
      return res.data
    }
  )
}
export const useTransferSupplierContactMutation = (
  options?: UseMutationOptions<
    LibraryResponses.Count,
    AxiosError,
    transferContactValidationType
  >
) => {
  return useMutation({
    ...options,
    mutationFn: async (req) => {
      return (await httpClient.post("suppliers/transfercontact", req)).data
    },
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries(["suppliers"])
      await queryClient.invalidateQueries(["suppliersList"])
      await queryClient.invalidateQueries(["contactsList"])
      await queryClient.invalidateQueries(["listProductionSites"])
      options?.onSuccess?.(data, variables, context)
    },
  })
}

export const useTransferSupplierContactCertificatesMutation = (
  options?: UseMutationOptions<
    LibraryResponses.Count,
    AxiosError,
    transferContactValidationType
  >
) => {
  const { t } = useTranslation("traceability")
  return useMutation({
    ...options,
    mutationFn: async (req) => {
      return (
        await httpClient.post("suppliers/transfercontact/certificates", req)
      ).data
    },
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries(["suppliers"])
      await queryClient.invalidateQueries(["suppliersList"])
      await queryClient.invalidateQueries(["contactsList"])
      await queryClient.invalidateQueries(["listProductionSites"])
      options?.onSuccess?.(data, variables, context)
    },
    onError: (error, variables, context) => {
      options?.onError?.(error, variables, context)
      notifications.show({
        title: t("errors.title"),
        message: t("errors.axiosMessage", { message: error.response?.data }),
        color: "red",
      })
    },
  })
}
